// src/components/ProspectForm.jsx

import React, { useId, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Send,
} from "lucide-react";

import { app } from "../../src/Firebase-config";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const db = getFirestore(app);

/* ============================================================
   CONFIGURACIÓN

   Recibe el idioma desde MainScreen:
   <ProspectForm language={language} />

   No utiliza:
   - autoFocus
   - focus() programático
   - scrollIntoView()

   Los identificadores guardados en Firestore no cambian
   dependiendo del idioma.
============================================================ */

const REGIMEN_IDS = ["pfae", "resico", "no_se"];

const PROFESION_IDS = [
  "salud",
  "creativo",
  "fitness",
  "tech",
  "educacion",
  "legal",
  "construccion",
  "otro",
];

const ESTADOS_MX = [
  "Aguascalientes",
  "Baja California",
  "Baja California Sur",
  "Campeche",
  "Chiapas",
  "Chihuahua",
  "Ciudad de México",
  "Coahuila",
  "Colima",
  "Durango",
  "Estado de México",
  "Guanajuato",
  "Guerrero",
  "Hidalgo",
  "Jalisco",
  "Michoacán",
  "Morelos",
  "Nayarit",
  "Nuevo León",
  "Oaxaca",
  "Puebla",
  "Querétaro",
  "Quintana Roo",
  "San Luis Potosí",
  "Sinaloa",
  "Sonora",
  "Tabasco",
  "Tamaulipas",
  "Tlaxcala",
  "Veracruz",
  "Yucatán",
  "Zacatecas",
];

const DISPLAY_STYLE = {
  fontFamily:
    '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};

const INPUT_BASE = [
  "block min-h-12 w-full min-w-0 rounded-xl border",
  "px-4 py-3 text-base leading-6 text-[#081B3E]",
  "placeholder:text-slate-400 outline-none",
  "transition-colors duration-150",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");

const INPUT_NORMAL = [
  "border-slate-200 bg-white",
  "focus:border-[#1648EE] focus:ring-2 focus:ring-[#1648EE]/15",
].join(" ");

const INPUT_ERROR = [
  "border-rose-400 bg-rose-50",
  "focus:border-rose-500 focus:ring-2 focus:ring-rose-100",
].join(" ");

/* ============================================================
   TRADUCCIONES

   En las opciones:
   [texto principal, descripción]

   Su posición corresponde a REGIMEN_IDS y PROFESION_IDS.
============================================================ */

const TEXTS = {
  es: {
    formLabel: "Solicitud de asesoría SolCont",
    steps: [
      "Nombre",
      "Régimen",
      "Actividad",
      "Ubicación",
      "Contacto",
      "Confirmar",
    ],
    of: "de",
    back: "Anterior",
    next: "Continuar",
    send: "Solicitar asesoría",
    sending: "Enviando…",
    review: "Revisa tus datos",

    questions: [
      "¿Cómo te llamas?",
      "¿Bajo qué régimen tributas?",
      "¿A qué te dedicas?",
      "¿Dónde te encuentras?",
      "¿Cómo te contactamos?",
      "Revisa tu información.",
    ],

    descriptions: [
      "Comencemos con tus datos.",
      "Si no lo sabes, podemos ayudarte a identificarlo.",
      "Selecciona la opción que mejor describa tu actividad.",
      "Indica tu ciudad y estado en México.",
      "Estos datos nos permitirán atender tu solicitud.",
      "Puedes regresar a los pasos anteriores para corregir tus datos.",
    ],

    labels: {
      nombre: "Nombre(s)",
      apellidos: "Apellidos",
      regimen: "Régimen",
      profesion: "Actividad",
      profesionOtro: "Describe tu actividad",
      ciudad: "Ciudad",
      estado: "Estado",
      telefono: "Número de teléfono",
      whatsapp: "¿Este número tiene WhatsApp?",
      correo: "Correo electrónico",
      fullName: "Nombre",
      location: "Ubicación",
    },

    placeholders: {
      nombre: "María",
      apellidos: "García López",
      profesionOtro: "Cuéntanos a qué te dedicas",
      ciudad: "Puebla",
      estado: "Selecciona un estado",
      telefono: "+52 238 123 4567",
      correo: "nombre@ejemplo.com",
    },

    phoneHelp:
      "Incluye el código de país si tu número es extranjero.",

    whatsappOptions: [
      "Sí, tiene WhatsApp",
      "No, solo llamadas",
    ],

    regimenes: [
      [
        "PFAE",
        "Persona Física con Actividad Empresarial",
      ],
      [
        "RESICO",
        "Régimen Simplificado de Confianza",
      ],
      [
        "No estoy seguro",
        "Necesito ayuda para identificarlo",
      ],
    ],

    profesiones: [
      [
        "Salud",
        "Medicina, nutrición, fisioterapia y psicología",
      ],
      [
        "Diseño y creativos",
        "Diseño, fotografía, video e ilustración",
      ],
      [
        "Fitness y bienestar",
        "Entrenamiento, coaching e instrucción de yoga",
      ],
      [
        "Tecnología y digital",
        "Desarrollo, marketing digital y consultoría",
      ],
      [
        "Educación",
        "Tutorías, capacitación y conferencias",
      ],
      [
        "Legal y finanzas",
        "Abogacía, asesoría financiera y consultoría",
      ],
      [
        "Construcción y arquitectura",
        "Arquitectura, ingeniería y contratistas",
      ],
      [
        "Otro",
        "Mi actividad no está en la lista",
      ],
    ],

    errors: {
      nombreRequired: "Ingresa tu nombre.",
      apellidosRequired: "Ingresa tus apellidos.",
      selectOption: "Selecciona una opción.",
      activityRequired: "Describe tu actividad.",
      cityRequired: "Ingresa tu ciudad.",
      stateRequired: "Selecciona un estado válido.",
      phoneInvalid:
        "Ingresa un teléfono válido de 10 a 15 dígitos.",
      whatsappRequired:
        "Indica si este número tiene WhatsApp.",
      emailInvalid:
        "Ingresa un correo electrónico válido.",
      saveFailed:
        "No pudimos confirmar el envío. Tus datos siguen aquí; revisa tu conexión e inténtalo nuevamente.",
    },

    confirmation:
      "Nuestro equipo revisará tu solicitud y se pondrá en contacto contigo para confirmar el siguiente paso, el alcance y los honorarios del servicio.",

    success: {
      title: "¡Listo, {name}!",
      description:
        "Recibimos tu solicitud. Nuestro equipo revisará tu información y se pondrá en contacto contigo.",
      nextTitle: "Qué sigue",
      items: [
        "Revisamos la información que compartiste.",
        "Te contactamos por los medios indicados.",
        "Definimos el siguiente paso y las condiciones del servicio.",
      ],
    },
  },

  en: {
    formLabel: "SolCont consultation request",
    steps: [
      "Name",
      "Tax regime",
      "Activity",
      "Location",
      "Contact",
      "Review",
    ],
    of: "of",
    back: "Back",
    next: "Continue",
    send: "Request a consultation",
    sending: "Sending…",
    review: "Review your details",

    questions: [
      "What is your name?",
      "Which tax regime are you registered under?",
      "What do you do?",
      "Where are you located?",
      "How can we contact you?",
      "Review your information.",
    ],

    descriptions: [
      "Let us start with your details.",
      "If you are unsure, we can help you identify it.",
      "Choose the option that best describes your activity.",
      "Enter your city and state in Mexico.",
      "We will use these details to respond to your request.",
      "You can return to previous steps to correct your details.",
    ],

    labels: {
      nombre: "First name(s)",
      apellidos: "Last name(s)",
      regimen: "Tax regime",
      profesion: "Activity",
      profesionOtro: "Describe your activity",
      ciudad: "City",
      estado: "State",
      telefono: "Phone number",
      whatsapp: "Does this number have WhatsApp?",
      correo: "Email address",
      fullName: "Name",
      location: "Location",
    },

    placeholders: {
      nombre: "Maria",
      apellidos: "Garcia",
      profesionOtro: "Tell us about your work or business",
      ciudad: "Puebla",
      estado: "Select a state",
      telefono: "+52 238 123 4567",
      correo: "name@example.com",
    },

    phoneHelp:
      "Include the country code if you use a foreign number.",

    whatsappOptions: [
      "Yes, it has WhatsApp",
      "No, phone calls only",
    ],

    regimenes: [
      [
        "PFAE",
        "Mexican tax regime for individuals with business activities",
      ],
      [
        "RESICO",
        "Mexico’s Simplified Trust Regime",
      ],
      [
        "I am not sure",
        "I need help identifying it",
      ],
    ],

    profesiones: [
      [
        "Healthcare",
        "Medicine, nutrition, physiotherapy and psychology",
      ],
      [
        "Design and creative services",
        "Design, photography, video and illustration",
      ],
      [
        "Fitness and wellness",
        "Personal training, coaching and yoga instruction",
      ],
      [
        "Technology and digital",
        "Development, digital marketing and consulting",
      ],
      [
        "Education",
        "Tutoring, training and public speaking",
      ],
      [
        "Legal and finance",
        "Legal services, financial advice and consulting",
      ],
      [
        "Construction and architecture",
        "Architecture, engineering and contracting",
      ],
      [
        "Other",
        "My activity is not listed",
      ],
    ],

    errors: {
      nombreRequired: "Enter your first name.",
      apellidosRequired: "Enter your last name.",
      selectOption: "Select an option.",
      activityRequired: "Describe your activity.",
      cityRequired: "Enter your city.",
      stateRequired: "Select a valid state.",
      phoneInvalid:
        "Enter a valid phone number with 10 to 15 digits.",
      whatsappRequired:
        "Indicate whether this number has WhatsApp.",
      emailInvalid:
        "Enter a valid email address.",
      saveFailed:
        "We could not confirm your submission. Your details are still here; check your connection and try again.",
    },

    confirmation:
      "Our team will review your request and contact you to confirm the next step, the scope of the service and the applicable fees.",

    success: {
      title: "Thank you, {name}!",
      description:
        "We have received your request. Our team will review your information and contact you.",
      nextTitle: "What happens next",
      items: [
        "We review the information you provided.",
        "We contact you using the details supplied.",
        "We agree on the next step and the terms of the service.",
      ],
    },
  },

  fr: {
    formLabel: "Demande de consultation SolCont",
    steps: [
      "Nom",
      "Régime fiscal",
      "Activité",
      "Localisation",
      "Coordonnées",
      "Vérification",
    ],
    of: "sur",
    back: "Précédent",
    next: "Continuer",
    send: "Demander une consultation",
    sending: "Envoi en cours…",
    review: "Vérifiez vos informations",

    questions: [
      "Comment vous appelez-vous ?",
      "Quel est votre régime fiscal ?",
      "Quelle est votre activité ?",
      "Où vous trouvez-vous ?",
      "Comment vous joindre ?",
      "Vérifiez vos informations.",
    ],

    descriptions: [
      "Commençons par vos renseignements.",
      "Si vous ne le savez pas, nous pouvons vous aider à l’identifier.",
      "Choisissez l’option qui correspond le mieux à votre activité.",
      "Indiquez votre ville et votre État au Mexique.",
      "Ces coordonnées nous permettront de répondre à votre demande.",
      "Vous pouvez revenir aux étapes précédentes pour corriger vos informations.",
    ],

    labels: {
      nombre: "Prénom(s)",
      apellidos: "Nom(s) de famille",
      regimen: "Régime fiscal",
      profesion: "Activité",
      profesionOtro: "Décrivez votre activité",
      ciudad: "Ville",
      estado: "État",
      telefono: "Numéro de téléphone",
      whatsapp: "Ce numéro dispose-t-il de WhatsApp ?",
      correo: "Adresse courriel",
      fullName: "Nom",
      location: "Localisation",
    },

    placeholders: {
      nombre: "Marie",
      apellidos: "Tremblay",
      profesionOtro: "Présentez brièvement votre activité",
      ciudad: "Puebla",
      estado: "Sélectionnez un État",
      telefono: "+52 238 123 4567",
      correo: "nom@exemple.com",
    },

    phoneHelp:
      "Ajoutez l’indicatif du pays si vous utilisez un numéro étranger.",

    whatsappOptions: [
      "Oui, avec WhatsApp",
      "Non, appels uniquement",
    ],

    regimenes: [
      [
        "PFAE",
        "Régime mexicain des personnes physiques exerçant une activité commerciale",
      ],
      [
        "RESICO",
        "Régime simplifié de confiance du Mexique",
      ],
      [
        "Je ne sais pas",
        "J’ai besoin d’aide pour l’identifier",
      ],
    ],

    profesiones: [
      [
        "Santé",
        "Médecine, nutrition, physiothérapie et psychologie",
      ],
      [
        "Design et création",
        "Design, photographie, vidéo et illustration",
      ],
      [
        "Conditionnement physique et bien-être",
        "Entraînement, coaching et enseignement du yoga",
      ],
      [
        "Technologie et numérique",
        "Développement, marketing numérique et conseil",
      ],
      [
        "Éducation",
        "Tutorat, formation et conférences",
      ],
      [
        "Droit et finance",
        "Services juridiques, conseil financier et consultation",
      ],
      [
        "Construction et architecture",
        "Architecture, ingénierie et entrepreneurs en construction",
      ],
      [
        "Autre",
        "Mon activité ne figure pas dans la liste",
      ],
    ],

    errors: {
      nombreRequired: "Indiquez votre prénom.",
      apellidosRequired: "Indiquez votre nom de famille.",
      selectOption: "Sélectionnez une option.",
      activityRequired: "Décrivez votre activité.",
      cityRequired: "Indiquez votre ville.",
      stateRequired: "Sélectionnez un État valide.",
      phoneInvalid:
        "Indiquez un numéro de téléphone valide de 10 à 15 chiffres.",
      whatsappRequired:
        "Indiquez si ce numéro dispose de WhatsApp.",
      emailInvalid:
        "Indiquez une adresse courriel valide.",
      saveFailed:
        "Nous n’avons pas pu confirmer l’envoi. Vos informations sont conservées dans ce formulaire ; vérifiez votre connexion et réessayez.",
    },

    confirmation:
      "Notre équipe examinera votre demande et vous contactera pour préciser la prochaine étape, le périmètre du service et les honoraires applicables.",

    success: {
      title: "Merci, {name} !",
      description:
        "Nous avons reçu votre demande. Notre équipe examinera vos informations et prendra contact avec vous.",
      nextTitle: "La suite",
      items: [
        "Nous examinons les renseignements transmis.",
        "Nous vous contactons aux coordonnées indiquées.",
        "Nous définissons la prochaine étape et les conditions du service.",
      ],
    },
  },
};

/* ============================================================
   UTILIDADES
============================================================ */

function normalizeLanguage(language) {
  if (typeof language !== "string") return "es";

  const base = language.trim().toLowerCase().split(/[-_]/)[0];

  return Object.prototype.hasOwnProperty.call(TEXTS, base)
    ? base
    : "es";
}

function createInitialForm() {
  return {
    nombre: "",
    apellidos: "",
    regimen: "",
    profesion: "",
    profesionOtro: "",
    ciudad: "",
    estado: "",
    telefono: "",
    whatsapp: null,
    correo: "",
  };
}

function isValidPhone(value) {
  const phone = value.trim();
  const digits = phone.replace(/\D/g, "");

  return (
    /^\+?[\d\s().-]+$/.test(phone) &&
    digits.length >= 10 &&
    digits.length <= 15
  );
}

function validateStep(form, step) {
  const errors = {};

  if (step === 0) {
    if (!form.nombre.trim()) {
      errors.nombre = "nombreRequired";
    }

    if (!form.apellidos.trim()) {
      errors.apellidos = "apellidosRequired";
    }
  }

  if (step === 1) {
    if (!REGIMEN_IDS.includes(form.regimen)) {
      errors.regimen = "selectOption";
    }
  }

  if (step === 2) {
    if (!PROFESION_IDS.includes(form.profesion)) {
      errors.profesion = "selectOption";
    }

    if (
      form.profesion === "otro" &&
      !form.profesionOtro.trim()
    ) {
      errors.profesionOtro = "activityRequired";
    }
  }

  if (step === 3) {
    if (!form.ciudad.trim()) {
      errors.ciudad = "cityRequired";
    }

    if (!ESTADOS_MX.includes(form.estado)) {
      errors.estado = "stateRequired";
    }
  }

  if (step === 4) {
    if (!isValidPhone(form.telefono)) {
      errors.telefono = "phoneInvalid";
    }

    if (typeof form.whatsapp !== "boolean") {
      errors.whatsapp = "whatsappRequired";
    }

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
        form.correo.trim()
      )
    ) {
      errors.correo = "emailInvalid";
    }
  }

  return errors;
}

function getUtmParams() {
  try {
    const params = new URLSearchParams(
      window.location.search
    );

    return {
      source: params.get("utm_source") || null,
      medium: params.get("utm_medium") || null,
      campaign: params.get("utm_campaign") || null,
      term: params.get("utm_term") || null,
      content: params.get("utm_content") || null,
    };
  } catch {
    return {};
  }
}

function getOptionLabel(ids, translations, value) {
  const index = ids.indexOf(value);

  return index >= 0 ? translations[index][0] : "—";
}

/* ============================================================
   COMPONENTE PRINCIPAL
============================================================ */

export default function ProspectForm({ language = "es" }) {
  const locale = normalizeLanguage(language);
  const t = TEXTS[locale];

  const instanceId = useId();
  const savingRef = useRef(false);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(createInitialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const lastStep = t.steps.length - 1;
  const progress = ((step + 1) / t.steps.length) * 100;

  const fieldId = (field) => `${instanceId}-${field}`;

  const getError = (field) => {
    const code = errors[field];
    return code ? t.errors[code] : undefined;
  };

  const setValue = (field, value) => {
    if (savingRef.current) return;

    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];

      return next;
    });

    setSaveError(false);
  };

  const next = () => {
    if (savingRef.current) return;

    const nextErrors = validateStep(form, step);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setSaveError(false);
      setStep((current) => Math.min(current + 1, lastStep));
    }
  };

  const back = () => {
    if (savingRef.current) return;

    setErrors({});
    setSaveError(false);
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async () => {
    // Bloqueo inmediato contra dobles clics.
    if (savingRef.current) return;

    // Revalidamos todos los pasos antes de guardar.
    for (let index = 0; index < lastStep; index += 1) {
      const stepErrors = validateStep(form, index);

      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setStep(index);
        return;
      }
    }

    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    setErrors({});

    try {
      // Conservamos colección, campos e identificadores del CRM.
      await addDoc(collection(db, "prospectos"), {
        ...form,
        nombre: form.nombre.trim(),
        apellidos: form.apellidos.trim(),
        profesionOtro:
          form.profesion === "otro"
            ? form.profesionOtro.trim()
            : "",
        ciudad: form.ciudad.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        createdAt: serverTimestamp(),
        page: window.location.pathname,
        referrer: document.referrer || null,
        utm: getUtmParams(),
      });

      setSubmitted(true);
    } catch {
      setSaveError(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    if (savingRef.current) return;

    if (step < lastStep) {
      next();
    } else {
      void handleSubmit();
    }
  };

  if (submitted) {
    return (
      <div lang={locale} className="w-full min-w-0">
        <SuccessView form={form} t={t} />
      </div>
    );
  }

  return (
    <form
      lang={locale}
      onSubmit={handleFormSubmit}
      aria-label={t.formLabel}
      aria-busy={saving}
      noValidate
      className="w-full min-w-0 text-[#081B3E]"
    >
      {/* PROGRESO */}

      <div className="mb-8">
        <div
          className="mb-4 flex items-center justify-between gap-4"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1648EE] text-xs font-bold text-white">
              {String(step + 1).padStart(2, "0")}
            </span>

            <span className="text-xs font-bold uppercase tracking-wider text-[#1648EE]">
              {t.steps[step]}
            </span>
          </div>

          <span className="shrink-0 text-xs text-slate-500">
            {step + 1} {t.of} {t.steps.length}
          </span>
        </div>

        <div
          role="progressbar"
          aria-label={t.formLabel}
          aria-valuemin={0}
          aria-valuemax={t.steps.length}
          aria-valuenow={step + 1}
          aria-valuetext={`${t.steps[step]}: ${step + 1} ${t.of} ${t.steps.length}`}
          className="h-1.5 overflow-hidden rounded-full bg-[#1648EE]/10"
        >
          <div
            className="h-full rounded-full bg-[#F72585] transition-all duration-300 motion-reduce:transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* PASO ACTIVO */}

      <fieldset
        disabled={saving}
        className="m-0 min-w-0 border-0 p-0"
      >
        <legend className="sr-only">
          {t.steps[step]}
        </legend>

        <StepShell
          title={t.questions[step]}
          description={t.descriptions[step]}
        >
          {/* NOMBRE */}

          {step === 0 && (
            <div className="grid gap-5 sm:grid-cols-2">
              <TextInput
                id={fieldId("nombre")}
                name="nombre"
                label={t.labels.nombre}
                placeholder={t.placeholders.nombre}
                value={form.nombre}
                onChange={(value) => setValue("nombre", value)}
                error={getError("nombre")}
                autoComplete="given-name"
                maxLength={100}
              />

              <TextInput
                id={fieldId("apellidos")}
                name="apellidos"
                label={t.labels.apellidos}
                placeholder={t.placeholders.apellidos}
                value={form.apellidos}
                onChange={(value) => setValue("apellidos", value)}
                error={getError("apellidos")}
                autoComplete="family-name"
                maxLength={150}
              />
            </div>
          )}

          {/* RÉGIMEN */}

          {step === 1 && (
            <ChoiceGroup
              label={t.questions[1]}
              error={getError("regimen")}
              errorId={fieldId("regimen-error")}
            >
              <div className="grid gap-3">
                {REGIMEN_IDS.map((id, index) => (
                  <OptionCard
                    key={id}
                    name={fieldId("regimen")}
                    value={id}
                    selected={form.regimen === id}
                    onChange={() => setValue("regimen", id)}
                    label={t.regimenes[index][0]}
                    description={t.regimenes[index][1]}
                  />
                ))}
              </div>
            </ChoiceGroup>
          )}

          {/* ACTIVIDAD */}

          {step === 2 && (
            <div className="space-y-5">
              <ChoiceGroup
                label={t.questions[2]}
                error={getError("profesion")}
                errorId={fieldId("profesion-error")}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  {PROFESION_IDS.map((id, index) => (
                    <OptionCard
                      key={id}
                      name={fieldId("profesion")}
                      value={id}
                      selected={form.profesion === id}
                      onChange={() => setValue("profesion", id)}
                      label={t.profesiones[index][0]}
                      description={t.profesiones[index][1]}
                    />
                  ))}
                </div>
              </ChoiceGroup>

              {form.profesion === "otro" && (
                <TextInput
                  id={fieldId("profesionOtro")}
                  name="profesionOtro"
                  label={t.labels.profesionOtro}
                  placeholder={t.placeholders.profesionOtro}
                  value={form.profesionOtro}
                  onChange={(value) =>
                    setValue("profesionOtro", value)
                  }
                  error={getError("profesionOtro")}
                  maxLength={250}
                />
              )}
            </div>
          )}

          {/* UBICACIÓN */}

          {step === 3 && (
            <div className="grid gap-5 sm:grid-cols-2">
              <TextInput
                id={fieldId("ciudad")}
                name="ciudad"
                label={t.labels.ciudad}
                placeholder={t.placeholders.ciudad}
                value={form.ciudad}
                onChange={(value) => setValue("ciudad", value)}
                error={getError("ciudad")}
                autoComplete="address-level2"
                maxLength={120}
              />

              <div className="min-w-0">
                <label
                  htmlFor={fieldId("estado")}
                  className="mb-2 block text-sm font-semibold"
                >
                  {t.labels.estado}
                </label>

                <select
                  id={fieldId("estado")}
                  name="estado"
                  value={form.estado}
                  onChange={(event) =>
                    setValue("estado", event.target.value)
                  }
                  autoComplete="address-level1"
                  aria-required="true"
                  aria-invalid={Boolean(errors.estado)}
                  aria-describedby={
                    errors.estado
                      ? fieldId("estado-error")
                      : undefined
                  }
                  className={`${INPUT_BASE} ${
                    errors.estado ? INPUT_ERROR : INPUT_NORMAL
                  }`}
                >
                  <option value="">
                    {t.placeholders.estado}
                  </option>

                  {ESTADOS_MX.map((estado) => (
                    <option key={estado} value={estado}>
                      {estado}
                    </option>
                  ))}
                </select>

                <FieldError id={fieldId("estado-error")}>
                  {getError("estado")}
                </FieldError>
              </div>
            </div>
          )}

          {/* CONTACTO */}

          {step === 4 && (
            <div className="space-y-6">
              <TextInput
                id={fieldId("telefono")}
                name="telefono"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                label={t.labels.telefono}
                placeholder={t.placeholders.telefono}
                value={form.telefono}
                onChange={(value) => setValue("telefono", value)}
                error={getError("telefono")}
                help={t.phoneHelp}
                maxLength={30}
              />

              <ChoiceGroup
                label={t.labels.whatsapp}
                visibleLabel
                error={getError("whatsapp")}
                errorId={fieldId("whatsapp-error")}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  {[true, false].map((value, index) => (
                    <OptionCard
                      key={String(value)}
                      name={fieldId("whatsapp")}
                      value={String(value)}
                      selected={form.whatsapp === value}
                      onChange={() => setValue("whatsapp", value)}
                      label={t.whatsappOptions[index]}
                    />
                  ))}
                </div>
              </ChoiceGroup>

              <TextInput
                id={fieldId("correo")}
                name="correo"
                type="email"
                inputMode="email"
                autoComplete="email"
                label={t.labels.correo}
                placeholder={t.placeholders.correo}
                value={form.correo}
                onChange={(value) => setValue("correo", value)}
                error={getError("correo")}
                maxLength={254}
              />
            </div>
          )}

          {/* CONFIRMACIÓN */}

          {step === lastStep && (
            <ConfirmView form={form} t={t} />
          )}
        </StepShell>
      </fieldset>

      {/* ERROR DE ENVÍO */}

      {saveError && (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700"
        >
          {t.errors.saveFailed}
        </div>
      )}

      {/* NAVEGACIÓN */}

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#081B3E]/10 pt-6">
        {step > 0 ? (
          <button
            type="button"
            onClick={back}
            disabled={saving}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-semibold text-slate-600 transition hover:text-[#1648EE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1648EE] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowLeft
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
            />

            {t.back}
          </button>
        ) : (
          <span aria-hidden="true" />
        )}

        <button
          type="submit"
          disabled={saving}
          className={[
            "ml-auto inline-flex min-h-12 max-w-full",
            "items-center justify-center gap-3 rounded-xl",
            "px-5 py-3 text-sm font-bold text-white",
            "transition-colors focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[#F72585]",
            "focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-60",
            step === lastStep
              ? "bg-[#F72585] hover:bg-[#DE1672]"
              : "bg-[#1648EE] hover:bg-[#103BC9]",
          ].join(" ")}
        >
          {saving ? (
            <Loader2
              aria-hidden="true"
              className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
            />
          ) : step === lastStep ? (
            <Send
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
            />
          ) : null}

          <span>
            {saving
              ? t.sending
              : step === lastStep
                ? t.send
                : t.next}
          </span>

          {!saving && step < lastStep && (
            <ArrowRight
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
            />
          )}
        </button>
      </div>
    </form>
  );
}

/* ============================================================
   CONFIRMACIÓN
============================================================ */

function ConfirmView({ form, t }) {
  const regimenLabel = getOptionLabel(
    REGIMEN_IDS,
    t.regimenes,
    form.regimen
  );

  const profesionLabel =
    form.profesion === "otro"
      ? form.profesionOtro.trim()
      : getOptionLabel(
          PROFESION_IDS,
          t.profesiones,
          form.profesion
        );

  const rows = [
    {
      id: "nombre",
      label: t.labels.fullName,
      value: `${form.nombre.trim()} ${form.apellidos.trim()}`,
    },
    {
      id: "regimen",
      label: t.labels.regimen,
      value: regimenLabel,
    },
    {
      id: "actividad",
      label: t.labels.profesion,
      value: profesionLabel,
    },
    {
      id: "ubicacion",
      label: t.labels.location,
      value: `${form.ciudad.trim()}, ${form.estado}`,
    },
    {
      id: "telefono",
      label: t.labels.telefono,
      value: `${form.telefono.trim()}${
        form.whatsapp ? " · WhatsApp" : ""
      }`,
    },
    {
      id: "correo",
      label: t.labels.correo,
      value: form.correo.trim(),
    },
  ];

  return (
    <>
      <dl
        aria-label={t.review}
        className="overflow-hidden rounded-2xl border border-[#081B3E]/10 bg-[#F7F3E9]"
      >
        {rows.map((row, index) => (
          <div
            key={row.id}
            className={[
              "grid gap-2 px-5 py-4",
              "sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]",
              "sm:gap-5",
              index > 0 ? "border-t border-[#081B3E]/10" : "",
            ].join(" ")}
          >
            <dt className="text-[11px] font-bold uppercase leading-5 tracking-wider text-slate-500">
              {row.label}
            </dt>

            <dd className="min-w-0 break-words text-sm font-medium leading-6 sm:text-right">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      <p className="mt-5 text-xs leading-6 text-slate-500">
        {t.confirmation}
      </p>
    </>
  );
}

/* ============================================================
   ENVÍO EXITOSO
============================================================ */

function SuccessView({ form, t }) {
  const title = t.success.title.replace(
    "{name}",
    form.nombre.trim()
  );

  return (
    <section className="py-8 text-center text-[#081B3E] sm:py-12">
      <div
        aria-hidden="true"
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1648EE] text-white"
      >
        <Check className="h-8 w-8" strokeWidth={2} />
      </div>

      <div role="status" aria-live="polite" aria-atomic="true">
        <h3
          style={DISPLAY_STYLE}
          className="mt-6 break-words text-3xl font-black uppercase leading-tight tracking-tight"
        >
          {title}
        </h3>

        <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-slate-600">
          {t.success.description}
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-md rounded-2xl border border-[#081B3E]/10 bg-[#F7F3E9] p-6 text-left">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full bg-[#F72585]"
          />

          <h4 className="text-xs font-bold uppercase tracking-wider text-[#1648EE]">
            {t.success.nextTitle}
          </h4>
        </div>

        <ul className="mt-5 space-y-4">
          {t.success.items.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-3 text-sm leading-6"
            >
              <span className="mt-0.5 text-xs font-bold text-[#F72585]">
                {String(index + 1).padStart(2, "0")}
              </span>

              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ============================================================
   COMPONENTES DEL FORMULARIO
============================================================ */

function StepShell({ title, description, children }) {
  return (
    <div className="min-w-0">
      <h3
        style={DISPLAY_STYLE}
        className="text-2xl font-black uppercase leading-tight tracking-tight text-[#081B3E] sm:text-3xl"
      >
        {title}
      </h3>

      {description && (
        <p className="mt-3 text-sm leading-6 text-slate-500">
          {description}
        </p>
      )}

      <div className="mt-7 min-w-0">
        {children}
      </div>
    </div>
  );
}

function ChoiceGroup({
  label,
  visibleLabel = false,
  error,
  errorId,
  children,
}) {
  return (
    <fieldset
      aria-describedby={error ? errorId : undefined}
      className="m-0 min-w-0 border-0 p-0"
    >
      <legend
        className={
          visibleLabel
            ? "mb-3 text-sm font-semibold text-[#081B3E]"
            : "sr-only"
        }
      >
        {label}
      </legend>

      {children}

      <FieldError id={errorId}>
        {error}
      </FieldError>
    </fieldset>
  );
}

function OptionCard({
  name,
  value,
  selected,
  onChange,
  label,
  description,
}) {
  return (
    <label className="block h-full min-w-0 cursor-pointer">
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={onChange}
        className="peer sr-only"
      />

      <span
        className={[
          "flex h-full min-h-14 items-start gap-3",
          "rounded-xl border px-4 py-4 text-left",
          "transition-colors duration-150",
          "peer-focus-visible:ring-2",
          "peer-focus-visible:ring-[#F72585]",
          "peer-focus-visible:ring-offset-2",
          "peer-disabled:cursor-not-allowed peer-disabled:opacity-60",
          selected
            ? "border-[#1648EE] bg-[#1648EE] text-white"
            : "border-slate-200 bg-white text-[#081B3E] hover:border-[#1648EE]/50 hover:bg-[#F7F3E9]",
        ].join(" ")}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold leading-6">
            {label}
          </span>

          {description && (
            <span
              className={[
                "mt-1 block text-xs leading-5",
                selected ? "text-white/85" : "text-slate-500",
              ].join(" ")}
            >
              {description}
            </span>
          )}
        </span>

        <span
          aria-hidden="true"
          className={[
            "mt-0.5 flex h-5 w-5 shrink-0",
            "items-center justify-center rounded-full border",
            selected
              ? "border-[#F72585] bg-[#F72585] text-white"
              : "border-slate-300 bg-white",
          ].join(" ")}
        >
          {selected && (
            <Check className="h-3 w-3" strokeWidth={3} />
          )}
        </span>
      </span>
    </label>
  );
}

function TextInput({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  error,
  help,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
}) {
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  const describedBy = [
    help ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-semibold text-[#081B3E]"
      >
        {label}
      </label>

      <input
        id={id}
        name={name}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        aria-required="true"
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
        spellCheck={type === "email" ? false : undefined}
        autoCapitalize={type === "email" ? "none" : undefined}
        autoCorrect={type === "email" ? "off" : undefined}
        className={`${INPUT_BASE} ${
          error ? INPUT_ERROR : INPUT_NORMAL
        }`}
      />

      {help && (
        <p
          id={helpId}
          className="mt-2 text-xs leading-5 text-slate-500"
        >
          {help}
        </p>
      )}

      <FieldError id={errorId}>
        {error}
      </FieldError>
    </div>
  );
}

function FieldError({ id, children }) {
  if (!children) return null;

  return (
    <p
      id={id}
      role="alert"
      className="mt-2 text-xs font-medium leading-5 text-rose-600"
    >
      {children}
    </p>
  );
}